import { DecimalPipe } from '@angular/common';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  ViewChildren,
  QueryList,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';

// COMPONENTS
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';

import { OptionInterface } from '../../../shared/interfaces/common.interface';
import { trackByControlValue } from '../../../shared/util';
import {
  PickListItemInterface,
  PickListItemStatus,
  PickListItemTableInterface,
  PicklistItemUiInterface,
  PicklistStatus,
  PickListItemDraftInterface,
} from '../../interfaces/picklist.interface';
import { PicklistItemSortPipe } from '../../pipes';

import { UiInputComponent } from './../../../ui/components/ui-input/ui-input.component';

// INTERFACES

@Component({
  selector: 'lum-picklist-item-table',
  templateUrl: './picklist-item-table.component.html',
  styleUrls: ['./picklist-item-table.component.scss'],
  providers: [DecimalPipe, PicklistItemSortPipe],
})
export class PicklistItemTableComponent implements OnChanges {
  constructor(private decimalPipe :DecimalPipe, private pickListItemSortPipe :PicklistItemSortPipe) {}
  @Input()
  items :PickListItemInterface[] = [];

  @Input()
  locations :any[]; // TODO: CREATE  INTERFACE

  @Input()
  lots :any[] = []; // TODO: CREATE  INTERFACE

  @Input()
  dataSource :PickListItemTableInterface[] = [];

  @Input()
  form :UntypedFormGroup;

  @Input()
  rows :UntypedFormArray;

  @Input()
  loadingLocations :boolean;

  @Input()
  loadingLots :boolean;

  @Input()
  currentPickListStatus :PicklistStatus;

  @Input()
  lotTrackingEnable :boolean = false;

  @Input()
  submittingForm :boolean = false;

  @Input()
  loading :boolean = false;

  @Input()
  draftItems :PickListItemDraftInterface[] = [];

  @Input()
  hasLotTracking :boolean = false;

  @Input()
  sortDirection :'asc' | 'desc' = 'asc';

  @Output()
  pickEvent = new EventEmitter<PicklistItemUiInterface>();

  @Output()
  doneEvent = new EventEmitter<number>();

  @Output()
  resetEvent = new EventEmitter<number>();

  @Output()
  splitEvent = new EventEmitter<number>();

  @Output()
  openLocationListEvent = new EventEmitter<{
    rfqId :number;
    locationId :number;
  }>();

  @Output()
  openLotListEvent = new EventEmitter<{
    rfqId :number;
    locationId :number;
  }>();

  @Output()
  selectLocationEvent = new EventEmitter<{
    location :OptionInterface;
    id :number;
  }>();

  @Output()
  selectLotEvent = new EventEmitter<{
    lot :OptionInterface;
    id :number;
  }>();

  @Output()
  removeItemEvent = new EventEmitter<{
    item :PicklistItemUiInterface;
    index :number;
  }>();

  @Output()
  showSkuDetailEvent = new EventEmitter<PicklistItemUiInterface>();

  @Output()
  sortEvent = new EventEmitter<{
    sortField :string;
  }>();

  @ViewChildren('quantityInput') quantityInputs :QueryList<UiInputComponent>;

  trackByControlValue = trackByControlValue;

  pickListItemStatus = PickListItemStatus;
  picklistStatus = PicklistStatus;
  checkedAllItems :boolean = false;

  ngOnChanges({ rows } :SimpleChanges) :void {
    if(rows?.currentValue?.length !== rows?.previousValue?.length) { 
      this.pickListItemSortPipe.transform(rows.currentValue, 'value.selectedLocation.label', this.sortDirection);
    }
  }

  onLocationToggle(visible :boolean, rfqId :number, locationId :number) :void {
    if (visible) {
      this.onGetLocationsByRfqId(rfqId, locationId);
    }
  }

  onGetLocationsByRfqId(rfqId :number, locationId :number) :void {
    this.openLocationListEvent.emit({ rfqId, locationId });
  }

  onSelectLocationEvent(location :OptionInterface, id :number) :void {
    this.selectLocationEvent.emit({ location, id });
  }

  onSelectLotEvent(lot :OptionInterface, id :number) :void {
    this.selectLotEvent.emit({ lot, id });
  }

  onPickevent(row :PicklistItemUiInterface, quantityInput :UiInputComponent) :void {
    setTimeout(() => quantityInput.focusInput(), 0);
    this.pickEvent.emit(row);
  }

  onSplitEvent(id :number, index :number) :void {
    const count = this.rows.value.filter(
      (row :PicklistItemUiInterface) => row.id === id || row.parentId === id,
    ).length;

    setTimeout(() => {
      this.quantityInputs.toArray()[index + count].focusInput();
    }, 0);
    this.splitEvent.emit(id);
  }

  onTapInfo(e :TouchEvent, data :PicklistItemUiInterface) {
    e.preventDefault();
    e.stopPropagation();

    this.showSkuDetailEvent.emit(data);
  }

  onCheckItem(abstractControl :AbstractControl) :void {
    const item = abstractControl as UntypedFormControl;
    item.get('checked')?.patchValue((item.value.checked = !item.value.checked));

    if (this.checkedItems === 0) {
      this.checkedAllItems = false;
    }

    if (this.checkedItems === this.rows.controls.length) {
      this.checkedAllItems = true;
    }
  }

  onCheckAllItems(event :MatCheckboxChange) :void {
    this.checkedAllItems = event.checked;
    this.rows.controls.forEach((item :AbstractControl) => {
      item.get('checked')?.patchValue((item.value.checked = event.checked));
    });
  }

  get formattedLocations() :OptionInterface[] {
    if (this.locations?.length) {
      return this.locations
        .filter((location) => location.onhandQuantity > 0)
        .map((location) => {
          const subLabel = location.onhandQuantity;

          return {
            value: location.locationId,
            label: `${location.locationName} - (${location?.warehouseName})`,
            subLabel: this.decimalPipe.transform(subLabel, '1.0', 'en-US') || 0,
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label));
    } else {
      return [];
    }
  }

  get formattedLots() :OptionInterface[] {
    if (this.lots?.length) {
      return this.lots.map((lot) => {
        return {
          value: lot?.id,
          label: lot?.lotNumber,
          subLabel: lot?.productionDate,
        };
      });
    } else {
      return [];
    }
  }

  get checkedItems() :number {
    return this.rows?.controls?.filter(
      (item :AbstractControl) => item?.value?.checked,
    ).length;
  }
}
